import { Col, Form, Input, InputNumber, Modal, Row, Select, Space } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import _ from 'lodash'
import { FC, useCallback, useContext, useState } from 'react'
import { EditableCellCtx } from '../../../../../../components/editable-cell/editable-cell'
import { requiredOneElementRules } from '../../../../../../constants/forms/validation/rules'
import { useRollerVariantsWithZeroFirst } from '../../../../../../hooks/use-roller-variants'
import { formatRollerDisplay } from '../../../../../../helpers/format-roller-display'
import { VirtualStamp } from '../../../../../../types/api/components/stamps/virtual-stamp'

/** Редактирование виртуального штампа в модалке (Ш×Д, Ручьи, Вал) — по аналогии с выбором штампа по характеристикам. */
interface VirtualStampModalProps {
    dataIndex?: NamePath
    record?: {
        virtualStamp?: VirtualStamp
    }
}

const VirtualStampModal: FC<VirtualStampModalProps> = ({ dataIndex, record }) => {
    const ctx = useContext(EditableCellCtx)
    const [open, setOpen] = useState(true)
    const form = Form.useFormInstance()

    const baseName = (Array.isArray(dataIndex) ? dataIndex : ['virtualStamp']) as NamePath
    const widthName = [...baseName, 'width']
    const lengthName = [...baseName, 'length']
    const floodsName = [...baseName, 'floods']
    const rollerVariantName = [...baseName, 'rollerVariant']
    const rollerSetName = [...baseName, 'roller', 'set']
    const rangeName = [...baseName, 'range']

    const length = Form.useWatch(lengthName, form) ?? record?.virtualStamp?.length
    const variants = useRollerVariantsWithZeroFirst(length)

    const rollerOptions = variants.map(item => ({
        key: `${item.roller.id}-${item.numberOfLabels}-${item.distance}-${length}`,
        value: JSON.stringify({
            roller: item.roller.documentId,
            distance: parseFloat(item.distance.toFixed(3)),
        }),
        label: (
            <Space>
                <div style={{ width: 90 }}>Межэт: {item.distance.toFixed(3)}</div>
                <div style={{ width: 90 }}>В ручье: {item.numberOfLabels}</div>
                <div style={{ width: 90 }}>Вал: {formatRollerDisplay(item.roller.roller)}</div>
                <div style={{ width: 120 }}>
                    {_.uniq(item.roller.machines?.map(m => m.machine_brand?.name)).join(', ')}
                </div>
            </Space>
        ),
    }))

    const handleOk = async () => {
        try {
            await form.validateFields([widthName, lengthName, floodsName, rollerSetName])
            ctx.onBlur()
            setOpen(false)
        } catch {
            // validation failed
        }
    }

    const handleCancel = useCallback(() => {
        setOpen(false)
        ctx.onCancel()
    }, [ctx])

    const onChangeLength = (value: number | null) => {
        form.setFieldValue(rollerSetName, undefined)
    }

    const onChangeRollerVariant = (value: string) => {
        const rollerInfo = JSON.parse(value)
        form.setFieldValue(rangeName, rollerInfo.distance)
        form.setFieldValue(rollerSetName, rollerInfo.roller)
    }

    return (
        <Modal
            title='Виртуальный штамп'
            open={open}
            onCancel={handleCancel}
            onOk={handleOk}
            okText='Сохранить'
            cancelText='Отмена'
            width={560}
            destroyOnClose
            onClose={handleCancel}
        >
            <Row gutter={16}>
                <Col span={6}>
                    <Form.Item
                        name={widthName}
                        label='Ширина'
                        rules={[{ required: true, message: 'Укажите ширину' }]}
                    >
                        <InputNumber min={1} controls={false} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        name={lengthName}
                        label='Длина'
                        rules={[{ required: true, message: 'Укажите длину' }]}
                    >
                        <InputNumber
                            min={1}
                            controls={false}
                            style={{ width: '100%' }}
                            onChange={onChangeLength}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        name={floodsName}
                        label='Ручьи'
                        rules={[{ required: true, message: 'Укажите ручьи' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        name={rollerVariantName}
                        label='Вал'
                        labelCol={{ span: 24 }}
                        rules={requiredOneElementRules}
                    >
                        <Select
                            allowClear
                            options={rollerOptions}
                            placeholder='Выберите вариант (межэт, в ручье, вал)'
                            style={{ width: '100%' }}
                            onChange={onChangeRollerVariant}
                        />
                    </Form.Item>
                </Col>
                <Form.Item name={rollerSetName} hidden>
                    <Input />
                </Form.Item>
                <Form.Item name={rangeName} hidden>
                    <Input />
                </Form.Item>
            </Row>
        </Modal>
    )
}

export default VirtualStampModal
